/**
 * Rating component
 *
 * @author Sebastiano Lovatin <sebastiano@gestionaleauto.com>
 */

/**
 * Main
 */
import React from 'react';
import clsx from 'clsx';
import PropTypes from 'prop-types';
/**
 * Material UI
 */
import { makeStyles } from '@mui/styles';
import {
  red, yellow, lightGreen, green,
} from '@mui/material/colors';
import SentimentDissatisfiedIcon from '@mui/icons-material/SentimentDissatisfied';
import SentimentNeutralIcon from '@mui/icons-material/SentimentNeutral';
import SentimentSatisfiedAltIcon from '@mui/icons-material/SentimentSatisfiedAlt';
import SentimentVerySatisfiedIcon from '@mui/icons-material/SentimentVerySatisfied';
/**
 * Rater
 */
import Rater from 'react-rater';
import 'react-rater/lib/react-rater.css';

const useStyles = makeStyles((theme) => ({
  rating: {
    cursor: 'pointer',
    paddingRight: 5,
    fontSize: '36px !important',
    color: theme.palette.primary.contrastText,
    '&.dissatisfiedActive': {
      color: `${red[500]} !important`,
      fill: `${red[500]} !important`,
    },
    '&.neutralActive': {
      color: `${yellow[500]} !important`,
      fill: `${yellow[500]} !important`,
    },
    '&.satisfiedActive': {
      color: `${lightGreen[500]} !important`,
      fill: `${lightGreen[500]} !important`,
    },
    '&.verySatisfiedActive': {
      color: `${green[600]} !important`,
      fill: `${green[600]} !important`,
    },
  },
}));

function Icon({
  icon, willBeActive, isActive,
}) {
  const classes = useStyles();
  switch (icon) {
    case 'dissatisfied':
      return (
        <SentimentDissatisfiedIcon
          className={clsx(classes.rating, { dissatisfiedActive: willBeActive || isActive }, 'ga-ecommerce-rating-icon')}
        />
      );
    case 'neutral':
      return (
        <SentimentNeutralIcon
          className={clsx(classes.rating, { neutralActive: willBeActive || isActive }, 'ga-ecommerce-rating-icon')}
        />
      );
    case 'satisfied':
      return (
        <SentimentSatisfiedAltIcon
          className={clsx(classes.rating, { satisfiedActive: willBeActive || isActive }, 'ga-ecommerce-rating-icon')}
        />
      );
    default:
      return (
        <SentimentVerySatisfiedIcon
          className={clsx(classes.rating, { verySatisfiedActive: willBeActive || isActive }, 'ga-ecommerce-rating-icon')}
        />
      );
  }
}
Icon.propTypes = {
  icon: PropTypes.string,
  willBeActive: PropTypes.bool,
  isActive: PropTypes.bool,
};
Icon.defaultProps = {
  icon: '',
  willBeActive: false,
  isActive: false,
};

/**
 * @param interactive {boolean} Create a read-only rater by setting this attribute to false
 * @param onRate {function} Callback to retrieve rating value. Called after rated
 * @returns {JSX.Element}
 */
function Rating({ interactive, onRate }) {
  return (
    <Rater
      total={4}
      interactive={interactive}
      onRate={({ rating }) => { onRate(rating); }}
    >
      <Icon icon="dissatisfied" />
      <Icon icon="neutral" />
      <Icon icon="satisfied" />
      <Icon />
    </Rater>
  );
}

Rating.propTypes = {
  interactive: PropTypes.bool,
  onRate: PropTypes.func,
};
Rating.defaultProps = {
  interactive: true,
  onRate: () => {},
};

export default Rating;
